import './styles/main.css';
import { capturePlayerName } from './player-name';
import { loadAnalytics, loadIndexes, loadRepoPushes } from './data/fetch';
import { SHOW_IN_THE_DETAIL } from './config';
import { latestInvestigation, loadInvestigations, setInvestigations } from './data/investigations';
import { attachPushes, attachStats } from './data/merge';
import { withProducts } from './data/products';
import { isPaused, loadLastFactoryRun } from './data/experiment';
import { pauseNoticeHtml } from './ui/callouts';
import { applyState, DEFAULT_STATE, topTags } from './filters';
import { paramsToState, stateToParams } from './router';
import { initTheme, toggleTheme } from './theme';
import { bindIconBurst, renderList, rouletteHighlight, type ViewMode } from './ui/rows';
import { renderFilterIndex } from './ui/filter-index';
import { burstConfetti, rainConfetti } from './ui/confetti';
import { renderHeader } from './ui/header';
import { renderDateline } from './ui/dateline';
import { renderBanner, renderEmpty, renderFatal, renderSkeleton } from './ui/states';
import { initPush } from './push';
import type { AppState, Entry } from './types';

initTheme();

const headerEl = document.getElementById('site-header')!;
const filterEl = document.getElementById('filter-index')!;
const datelineEl = document.getElementById('dateline')!;
const bannerEl = document.getElementById('banner')!;
const listEl = document.getElementById('grid')!;
const statusEl = document.getElementById('experiment-status')!;

const nowMs = Date.now();

const VIEW_KEY = 'lab:view';

let entries: Entry[] = [];
/** The day a factory last ran, once read from their build logs (null: unknown). */
let lastFactoryRun: string | null = null;
let hasAnalytics = false;
let hasWeekly = false;
let state: AppState = paramsToState(location.search);
// A game's footer link may carry the player's name here. Take it off the URL
// before anything builds a shareable directory link from location.href.
capturePlayerName();

let view: ViewMode = localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'rows';
let expandedSlug: string | null = null;
let lastQ = '';
let shuffling = false;

/** Add/remove a tag from the active filter. Shared by the header tag dropdown
 *  and the filter-index tag line so they never diverge. */
function toggleTag(tag: string): void {
  setState({
    tags: state.tags.includes(tag) ? state.tags.filter((t) => t !== tag) : [...state.tags, tag],
  });
}

/** Add/remove a category from the multi-select filter. Shared by the header
 *  category dropdown (mobile) and the filter-index tabs (desktop). */
function toggleCategory(category: string): void {
  setState({
    categories: state.categories.includes(category)
      ? state.categories.filter((c) => c !== category)
      : [...state.categories, category],
  });
}

const header = renderHeader(headerEl, state, view, entries, {
  onSearch: (q) => setState({ q }),
  onType: (type) => setState({ type }),
  onCategory: toggleCategory,
  onTag: toggleTag,
  onSort: (sort) => setState({ sort }),
  onViewToggle: () => setView(view === 'rows' ? 'cards' : 'rows'),
  onSurprise: surprise,
  onThemeToggle: toggleTheme,
});

function setView(v: ViewMode): void {
  view = v;
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    // storage unavailable
  }
  render();
}

bindIconBurst(listEl, (_slug, x, y) => burstConfetti(x, y));

function setState(patch: Partial<AppState>, opts: { push?: boolean } = {}): void {
  state = { ...state, ...patch };
  // Contextual tags belong to the selected categories. When the category set
  // changes, drop any active tag that's no longer offered so a stale tag can't
  // silently zero the results.
  if (patch.categories !== undefined && state.tags.length) {
    const valid = new Set(topTags(state, entries, Number.MAX_SAFE_INTEGER));
    const kept = state.tags.filter((t) => valid.has(t));
    if (kept.length !== state.tags.length) state.tags = kept;
  }
  expandedSlug = null; // filters change intent — collapse any open detail
  const url = `${location.pathname}${stateToParams(state)}`;
  if (opts.push === false) history.replaceState(null, '', url);
  else history.pushState(null, '', url);

  const q = state.q.trim().toLowerCase();
  if (q === 'party' && lastQ !== 'party') rainConfetti();
  lastQ = q;

  render();
}

function toggleDetail(slug: string): void {
  expandedSlug = expandedSlug !== slug ? slug : null;
  render();
}

function randomEntries(n: number): Entry[] {
  const pool = [...entries];
  const out: Entry[] = [];
  while (out.length < n && pool.length > 0) {
    out.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
  }
  return out;
}

async function surprise(): Promise<void> {
  if (shuffling) return; // ignore repeat taps — overlapping roulettes fight over scroll
  const pool = applyState(state, entries);
  const candidates = pool.length > 0 ? pool : entries;
  if (candidates.length === 0) return;
  shuffling = true;
  try {
    const winner = candidates[Math.floor(Math.random() * candidates.length)];
    await rouletteHighlight(listEl, winner.slug);
    expandedSlug = winner.slug;
    render();
    listEl
      .querySelector(`[data-slug="${CSS.escape(winner.slug)}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  } finally {
    shuffling = false;
  }
}

/** The paused notice heads the directory while no factory has run for a few
 *  days. Products don't count (they write no factory log), and their own view,
 *  which isn't the experiment, doesn't show it. */
function renderExperimentStatus(): void {
  const paused = lastFactoryRun !== null && isPaused(lastFactoryRun, nowMs) && state.type !== 'product';
  statusEl.innerHTML = paused ? pauseNoticeHtml(lastFactoryRun!) : '';
}

function render(): void {
  renderExperimentStatus();
  header.update(state, view, { entries, hasWeekly });
  renderFilterIndex(filterEl, state, entries, {
    onCategory: (category) => (category === null ? setState({ categories: [] }) : toggleCategory(category)),
    onTag: toggleTag,
    onCountryClear: () => setState({ country: null }),
  });
  const results = applyState(state, entries);
  if (results.length === 0) {
    renderEmpty(
      listEl,
      state.q,
      randomEntries(3),
      () => setState({ ...DEFAULT_STATE }),
      (slug) => toggleDetail(slug),
    );
  } else {
    renderList(
      listEl,
      results,
      {
        // Products never show analytics, so their own view drops the Trend and
        // Visits columns rather than leaving them empty.
        hasAnalytics: hasAnalytics && state.type !== 'product',
        productsView: state.type === 'product',
        hasWeekly,
        sort: state.sort,
        warmingNote: hasWeekly ? null : 'Analytics warming up — unlocks after the first full day of data',
        nowMs,
        expandedSlug,
        view,
      },
      {
        onDetail: toggleDetail,
        onCountry: (iso) => setState({ country: state.country === iso ? null : iso }),
        onSort: (sort) => setState({ sort }),
      },
    );
  }
}

async function boot(): Promise<void> {
  renderSkeleton(listEl);
  const [indexResult, analytics] = await Promise.all([loadIndexes(), loadAnalytics()]);
  const hosts = analytics ? Object.values(analytics.hosts) : [];
  hasAnalytics = hosts.length > 0;
  hasWeekly = hosts.some((h) => (h.agg?.v7 ?? 0) > 0);
  // Ben's own products join the factory builds (under the Products type); they
  // never take stats. With no factory index at all the directory is still down.
  entries = attachStats(withProducts(indexResult.entries), analytics);
  if (indexResult.entries.length === 0) {
    renderFatal(listEl, () => void boot());
    renderBanner(bannerEl, indexResult.failures, false, () => void boot());
    return;
  }
  renderBanner(bannerEl, indexResult.failures, indexResult.fromCache, () => void boot());
  if (!hasWeekly && (state.sort === 'trending' || state.sort === 'popular')) {
    state = { ...state, sort: 'new' };
  }
  render();
  // Whether the experiment is paused, from the factories' build logs. After first
  // paint, like the push times: a slow or rate-limited GitHub just means no notice.
  void loadLastFactoryRun().then((run) => {
    lastFactoryRun = run;
    renderExperimentStatus();
  });
  // Repo push times power the UPDATED badge. Fetched after first paint so a
  // slow or rate-limited GitHub API never blocks the directory.
  void loadRepoPushes().then((pushes) => {
    if (pushes.size === 0) return;
    entries = attachPushes(entries, pushes);
    render();
  });
  // In the Detail's investigations (dateline + "Related reading"). Fetched from
  // the newsroom after first paint — a slow or unreachable newsroom never blocks
  // or breaks the directory; it just means no dateline. Re-render so any open
  // detail panel picks up its related reading. Skipped entirely while
  // SHOW_IN_THE_DETAIL is off.
  if (SHOW_IN_THE_DETAIL) {
    void loadInvestigations().then((investigations) => {
      if (investigations.length === 0) return;
      setInvestigations(investigations);
      renderDateline(datelineEl, latestInvestigation());
      render();
    });
  }
}

window.addEventListener('popstate', () => {
  state = paramsToState(location.search);
  expandedSlug = null;
  render();
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && expandedSlug) {
    expandedSlug = null;
    render();
    return;
  }
  if (e.key !== '/') return;
  const active = document.activeElement;
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement) {
    return;
  }
  e.preventDefault();
  header.focusSearch();
});

// Web Push opt-in + home-screen badge. Self-contained and dormant until a VAPID
// key is configured, so it never blocks or alters first paint.
initPush();

void boot();
